// Lightweight language metadata shared with the embed runtime. No dictionaries.
export const ORGANIZATION_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen', direction: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', direction: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', direction: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', direction: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', direction: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', direction: 'rtl' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', direction: 'ltr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', direction: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', direction: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', direction: 'ltr' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', direction: 'ltr' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)', nativeName: '繁體中文', direction: 'ltr' },
] as const
export type OrganizationLanguageCode = (typeof ORGANIZATION_LANGUAGES)[number]['code']

export function isOrganizationLanguage(code: string): code is OrganizationLanguageCode {
  return ORGANIZATION_LANGUAGES.some((language) => language.code === code)
}
// Resolve explicit scripts before regions: zh-Hant-CN stays Traditional,
// while zh-TW/HK/MO infer Traditional and zh-CN/SG infer Simplified.
export function organizationLanguage(code?: string) {
  try {
    const locale = new Intl.Locale((code || 'en').trim().replaceAll('_', '-'))
    const key = locale.language === 'zh' && locale.maximize().script === 'Hant' ? 'zh-Hant' : locale.language
    return ORGANIZATION_LANGUAGES.find((language) => language.code === key) ?? ORGANIZATION_LANGUAGES[0]
  } catch {
    return ORGANIZATION_LANGUAGES[0]
  }
}

// Keep locale identity separate from preset lookup (es-MX may use Spanish copy).
export function canonicalOrganizationLocale(code = 'en'): string {
  const locale = Intl.getCanonicalLocales(code.trim())[0]
  if (!locale || locale.length > 35) throw new Error('Choose a valid language.')
  return locale
}

export function organizationTextDirection(code?: string, stored?: 'ltr' | 'rtl'): 'ltr' | 'rtl' {
  return supportedOrganizationLocale(code) ? organizationLanguage(code).direction : stored ?? 'ltr'
}

// A page hint may select an available preset, but must never turn an unknown
// language into English or change the organization's saved locale.
export function supportedOrganizationLocale(code?: string | null): string | undefined {
  if (!code || code.length > 35) return undefined
  try {
    const locale = canonicalOrganizationLocale(code.replaceAll('_', '-'))
    return isOrganizationLanguage(locale.split('-')[0]) ? locale : undefined
  } catch {
    return undefined
  }
}

// Merge preset-level edits before exact regional edits. Keep Chinese scripts
// separate, and preserve unknown legacy locales without borrowing English edits.
export function organizationLocaleFallbacks(code: string): string[] {
  const locale = canonicalOrganizationLocale(code)
  const preset = supportedOrganizationLocale(locale) ? organizationLanguage(locale).code : locale.split('-')[0]
  return [...new Set([preset, locale])]
}
